/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation, useQuery } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
  UserDefaultTeamIdDocument,
  UserTeamsDocument,
  TeamNameDocument,
  UpdateDefaultTeamDocument,
} from '@fai/friendli-suite/generated/client/graphql';
import { successProps } from '@fai/ui/components/notification/props';

export const useTeamName = (teamId: string) => {
  const { data: rawData } = useQuery(TeamNameDocument, {
    variables: { teamId },
  });

  const teamName = rawData?.clientTeam?.name ?? null;

  return { teamName };
};

export const useTeams = () => {
  const { data: rawData } = useQuery(UserTeamsDocument);

  const teams =
    rawData?.clientSession?.user.teams.edges.map(({ node }) => ({
      id: node.id,
      name: node.name ?? null,
    })) ?? null;

  return { teams };
};

export const useUpdateDefaultTeam = () => {
  const { t } = useTranslation();

  const [updateDefaultTeamMutation] = useMutation(UpdateDefaultTeamDocument, {
    refetchQueries: [{ query: UserTeamsDocument }, { query: UserDefaultTeamIdDocument }],
  });

  const onUpdateDefaultTeam = (teamId: string, teamName?: string) => {
    updateDefaultTeamMutation({
      variables: { input: { teamId } },
      onCompleted: () =>
        notifications.show({
          ...successProps,
          message: t('user_settings.team_is_set_to_default_team', {
            teamName,
          }),
        }),
    });
  };

  return { onUpdateDefaultTeam };
};
